import type { ClientJobListItem } from '@hemmafixa/shared';
import {
  Badge,
  DataTable,
  EmptyState,
  FilterBar,
  FilterChips,
  MasterDetail,
  PageHeader,
  Pagination,
  StatusBadge,
  type ColumnDef,
} from '@hemmafixa/ui';
import { Briefcase, MousePointerClick } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { fill, useI18n } from '@/i18n/context';
import { useCategoryLabel } from '../shared/catalogue';
import { DetailPanel, SelectableCard } from '../shared/master-detail-parts';
import { QueryView } from '../shared/query-view';
import { useStatuses } from '../shared/statuses';
import { useSelectedParam, useShownSelection } from '../shared/use-selected-param';
import { useMyJobs } from './api';
import { ClientJobDetail } from './client-job-page';

type Filter = 'all' | 'active' | 'closed';
const PAGE_SIZE = 10;

/** The client's jobs, newest first (P3-09). */
export function ClientJobsPage() {
  const { t, date } = useI18n();
  const categoryLabel = useCategoryLabel();
  const [picked, setSelected] = useSelectedParam();
  const [params] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [searchQuery, setSearchQuery] = useState(search);
  const [filter, setFilter] = useState<Filter>('all');
  const [page, setPage] = useState(1);
  const j = t.client.jobs;

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const query = useMyJobs({ filter, q: searchQuery || undefined, page, pageSize: PAGE_SIZE });
  const selected = useShownSelection(
    picked,
    query.data?.items.map((item) => item.id),
  );

  const columns = useMemo<ColumnDef<ClientJobListItem, unknown>[]>(
    () => [
      { accessorKey: 'title', header: j.columns.job },
      {
        accessorKey: 'categorySlug',
        header: j.columns.category,
        cell: ({ row }) => categoryLabel(row.original.categorySlug),
      },
      {
        accessorKey: 'status',
        header: j.columns.status,
        cell: ({ row }) => <JobStatus job={row.original} />,
      },
      { accessorKey: 'proposalCount', header: j.columns.proposals },
      {
        accessorKey: 'submittedAt',
        header: j.columns.posted,
        cell: ({ row }) => (row.original.submittedAt ? date(row.original.submittedAt) : ''),
      },
    ],
    [j, date, categoryLabel],
  );

  const filtering = search !== '' || filter !== 'all';

  return (
    <div className="grid gap-4">
      <PageHeader title={j.title} description={j.description} />
      <MasterDetail
        open={selected !== null}
        onClose={() => setSelected(null)}
        labels={{ details: j.title }}
        placeholder={<EmptyState icon={MousePointerClick} title={t.common.pickItem} />}
        detail={
          selected ? (
            <DetailPanel fullPage={`/client/jobs/${selected}`}>
              <ClientJobDetail id={selected} />
            </DetailPanel>
          ) : null
        }
        list={
          <>
            <FilterBar
              search={search}
              onSearchChange={(value) => {
                setSearch(value);
                setPage(1);
              }}
              searchPlaceholder={j.searchPlaceholder}
              summary={
                query.data
                  ? fill(t.common.showingCount, { shown: query.data.items.length, total: query.data.total })
                  : undefined
              }
              hasActiveFilters={filtering}
              onClear={() => {
                setSearch('');
                setFilter('all');
                setPage(1);
              }}
              clearLabel={t.common.clearFilters}
              chips={
                <FilterChips
                  label={j.columns.status}
                  value={filter}
                  onChange={(value) => {
                    setFilter(value);
                    setPage(1);
                  }}
                  options={(['all', 'active', 'closed'] as const).map((f) => ({
                    value: f,
                    label: j.filters[f],
                  }))}
                />
              }
            />
            <QueryView query={query}>
              {(result) => (
                <>
                  <DataTable
                    layout="cards"
                    columns={columns}
                    data={result.items}
                    getRowId={(job) => job.id}
                    renderCard={(job) => (
                      <SelectableCard
                        to={`/client/jobs/${job.id}`}
                        selected={job.id === selected}
                        onSelect={() => setSelected(job.id)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <p className="font-semibold">{job.title}</p>
                          <JobStatus job={job} />
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {categoryLabel(job.categorySlug)}
                          {job.submittedAt ? ` · ${date(job.submittedAt)}` : ''} ·{' '}
                          {fill(t.client.proposalCount, { count: job.proposalCount })}
                        </p>
                      </SelectableCard>
                    )}
                    empty={
                      filtering ? (
                        <EmptyState
                          icon={Briefcase}
                          title={t.common.noMatchesTitle}
                          description={t.common.noMatchesText}
                        />
                      ) : (
                        <EmptyState icon={Briefcase} title={j.empty} description={j.emptyText} />
                      )
                    }
                  />
                  <Pagination
                    page={result.page}
                    pageSize={result.pageSize}
                    total={result.total}
                    onPageChange={setPage}
                    labels={{
                      previous: t.common.previous,
                      next: t.common.next,
                      showing: (from, to, total) => fill(t.common.showingRange, { from, to, total }),
                    }}
                  />
                </>
              )}
            </QueryView>
          </>
        }
      />
    </div>
  );
}

/** The status, plus a reminder when admin is waiting for an answer */
function JobStatus({ job }: { job: ClientJobListItem }) {
  const { t } = useI18n();
  const statuses = useStatuses();
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <StatusBadge status={job.status} statuses={statuses.job} />
      {job.needsAnswer && <Badge tone="brand">{t.client.jobs.needsAnswer}</Badge>}
    </span>
  );
}
